import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Handshake, Building2, Crown, Sparkles, Plus, Search, ExternalLink,
  Edit2, Trash2, CheckCircle2, XCircle, Eye, Globe, Upload,
  AlertCircle, ArrowUpRight, Check, X, ShieldCheck, RefreshCw, Zap
} from 'lucide-react';

export interface Partner {
  id: number;
  name: string;
  tier: 'official' | 'connected';
  logoUrl?: string | null;
  websiteUrl?: string | null;
  description?: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface BusinessPartnersHubProps {
  authHeader: () => Record<string, string>;
}

export default function BusinessPartnersHub({ authHeader }: BusinessPartnersHubProps) {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [stats, setStats] = useState({ total: 0, official: 0, connected: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState<'all' | 'official' | 'connected'>('all');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    tier: 'official' | 'connected';
    logoUrl: string;
    websiteUrl: string;
    description: string;
    displayOrder: number;
    isActive: boolean;
  }>({
    name: '',
    tier: 'official',
    logoUrl: '',
    websiteUrl: '',
    description: '',
    displayOrder: 0,
    isActive: true,
  });

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [logoPreviewError, setLogoPreviewError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showFeedback = (type: 'success' | 'error', message: string) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 4000);
  };

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/partners', { headers: authHeader() });
      const data = await res.json();
      if (data.success && Array.isArray(data.partners)) {
        setPartners(data.partners);
        if (data.stats) setStats(data.stats);
      } else {
        showFeedback('error', data.error || 'Failed to fetch business partners');
      }
    } catch (err: any) {
      showFeedback('error', err?.message || 'Network error fetching partners');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const openAddModal = () => {
    setEditingPartner(null);
    setFormData({
      name: '',
      tier: 'official',
      logoUrl: '',
      websiteUrl: '',
      description: '',
      displayOrder: partners.length,
      isActive: true,
    });
    setLogoPreviewError(false);
    setIsModalOpen(true);
  };

  const openEditModal = (partner: Partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      tier: partner.tier,
      logoUrl: partner.logoUrl || '',
      websiteUrl: partner.websiteUrl || '',
      description: partner.description || '',
      displayOrder: partner.displayOrder,
      isActive: partner.isActive,
    });
    setLogoPreviewError(false);
    setIsModalOpen(true);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    const data = new FormData();
    data.append('logo', file);

    try {
      const headers = authHeader();
      // Remove Content-Type so browser sets multipart/form-data with boundary
      const uploadHeaders: Record<string, string> = {};
      for (const [key, value] of Object.entries(headers)) {
        if (key.toLowerCase() !== 'content-type') {
          uploadHeaders[key] = value;
        }
      }

      const res = await fetch('/api/admin/partners/upload-logo', {
        method: 'POST',
        headers: uploadHeaders,
        body: data,
      });
      const result = await res.json();

      if (result.success && result.url) {
        setFormData(prev => ({ ...prev, logoUrl: result.url }));
        setLogoPreviewError(false);
        showFeedback('success', 'Logo image uploaded successfully!');
      } else {
        showFeedback('error', result.error || 'Logo upload failed');
      }
    } catch (err: any) {
      showFeedback('error', err?.message || 'File upload error');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showFeedback('error', 'Company name is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingPartner
        ? `/api/admin/partners/${editingPartner.id}`
        : '/api/admin/partners';
      const method = editingPartner ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeader(),
        body: JSON.stringify(formData),
      });
      const result = await res.json();

      if (result.success) {
        showFeedback('success', result.message || 'Partner saved successfully!');
        setIsModalOpen(false);
        fetchPartners();
      } else {
        showFeedback('error', result.error || 'Failed to save partner');
      }
    } catch (err: any) {
      showFeedback('error', err?.message || 'Error saving partner');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (partner: Partner) => {
    try {
      const res = await fetch(`/api/admin/partners/${partner.id}/toggle-active`, {
        method: 'PATCH',
        headers: authHeader(),
      });
      const result = await res.json();
      if (result.success) {
        showFeedback('success', result.message);
        setPartners(prev =>
          prev.map(p => (p.id === partner.id ? { ...p, isActive: !p.isActive } : p))
        );
        setStats(prev => ({
          ...prev,
          active: partner.isActive ? prev.active - 1 : prev.active + 1,
        }));
      } else {
        showFeedback('error', result.error || 'Failed to toggle status');
      }
    } catch (err: any) {
      showFeedback('error', err?.message || 'Status toggle failed');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to remove this partner?')) return;

    setDeletingId(id);
    try {
      const res = await fetch(`/api/admin/partners/${id}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      const result = await res.json();
      if (result.success) {
        showFeedback('success', result.message || 'Partner removed');
        fetchPartners();
      } else {
        showFeedback('error', result.error || 'Failed to delete partner');
      }
    } catch (err: any) {
      showFeedback('error', err?.message || 'Error deleting partner');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredPartners = partners.filter(p => {
    const matchesTier = tierFilter === 'all' || p.tier === tierFilter;
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      p.name.toLowerCase().includes(q) ||
      (p.description && p.description.toLowerCase().includes(q)) ||
      (p.websiteUrl && p.websiteUrl.toLowerCase().includes(q));
    return matchesTier && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Toast Feedback Notification */}
      {feedback && (
        <div
          className={`fixed top-6 right-6 z-[300] px-4 py-3 rounded-xl text-xs font-mono font-bold flex items-center gap-2 shadow-2xl border transition-all animate-in fade-in slide-in-from-top-4 ${
            feedback.type === 'success'
              ? 'bg-emerald-950/90 border-emerald-500/50 text-emerald-300 shadow-[0_0_20px_rgba(16,185,129,0.3)]'
              : 'bg-rose-950/90 border-rose-500/50 text-rose-300 shadow-[0_0_20px_rgba(244,63,94,0.3)]'
          }`}
        >
          {feedback.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* ── Header & KPI Telemetry Grid ─────────────────────────────────── */}
      <div className="bg-[#090d16]/90 border border-slate-800/80 rounded-2xl p-6 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-amber-500/5 blur-3xl pointer-events-none" />

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-5 border-b border-slate-800/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-amber-500/20 to-emerald-500/20 border border-amber-500/30 flex items-center justify-center shadow-[0_0_15px_rgba(245,158,11,0.2)]">
              <Handshake className="text-amber-400" size={22} />
            </div>
            <div>
              <h2 className="text-base font-black text-white font-mono uppercase tracking-wider flex items-center gap-2">
                Ecosystem & Business Partners Governance
              </h2>
              <p className="text-xs text-slate-400 font-mono">
                Manage Official Business Partners (Premium) and Connected Ecosystem Partners on Kangavena Home
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button
              onClick={fetchPartners}
              disabled={loading}
              className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-700 transition-all cursor-pointer active:scale-95"
              title="Refresh Partners List"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin text-cyan-400' : ''} />
            </button>
            <button
              onClick={openAddModal}
              className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 via-emerald-600 to-teal-500 hover:from-amber-400 hover:to-teal-400 text-slate-950 font-black rounded-xl text-xs font-mono uppercase tracking-wider shadow-[0_0_25px_rgba(245,158,11,0.3)] transition-all hover:scale-[1.02] active:scale-95 cursor-pointer"
            >
              <Plus size={16} />
              <span>Add Business Partner</span>
            </button>
          </div>
        </div>

        {/* Telemetry Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
          <div className="bg-black/40 border border-white/5 rounded-xl p-4">
            <p className="text-[10px] text-slate-400 font-mono font-bold uppercase tracking-widest">Total Partners</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-black text-white font-mono">{stats.total}</span>
              <span className="text-[10px] text-slate-500 font-mono">registered</span>
            </div>
          </div>

          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 shadow-[0_0_15px_rgba(245,158,11,0.05)]">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-amber-400 font-mono font-bold uppercase tracking-widest">Official Partners</p>
              <Crown size={14} className="text-amber-400" />
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-black text-amber-300 font-mono">{stats.official}</span>
              <span className="text-[10px] text-amber-400/70 font-mono font-bold">PREMIUM ALLIANCE</span>
            </div>
          </div>

          <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-xl p-4 shadow-[0_0_15px_rgba(6,182,212,0.05)]">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-cyan-400 font-mono font-bold uppercase tracking-widest">Connected Partners</p>
              <Zap size={14} className="text-cyan-400" />
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-black text-cyan-300 font-mono">{stats.connected}</span>
              <span className="text-[10px] text-cyan-400/70 font-mono font-bold">ECOSYSTEM</span>
            </div>
          </div>

          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 shadow-[0_0_15px_rgba(16,185,129,0.05)]">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-emerald-400 font-mono font-bold uppercase tracking-widest">Active on Home</p>
              <Eye size={14} className="text-emerald-400" />
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-black text-emerald-400 font-mono">{stats.active}</span>
              <span className="text-[10px] text-emerald-400/70 font-mono font-bold">LIVE VISIBLE</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Search & Tier Tabs Filter Bar ───────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Tier Tabs */}
        <div className="flex items-center gap-1.5 p-1 bg-slate-900/90 border border-white/10 rounded-xl w-full sm:w-auto">
          <button
            onClick={() => setTierFilter('all')}
            className={`flex-1 sm:flex-initial px-3.5 py-1.5 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer ${
              tierFilter === 'all'
                ? 'bg-purple-600 text-white shadow-[0_0_15px_rgba(168,85,247,0.3)]'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            All Partners ({partners.length})
          </button>
          <button
            onClick={() => setTierFilter('official')}
            className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer ${
              tierFilter === 'official'
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-black shadow-[0_0_15px_rgba(245,158,11,0.3)]'
                : 'text-slate-400 hover:text-amber-400'
            }`}
          >
            <Crown size={12} />
            Official Premium ({stats.official})
          </button>
          <button
            onClick={() => setTierFilter('connected')}
            className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer ${
              tierFilter === 'connected'
                ? 'bg-cyan-600 text-white shadow-[0_0_15px_rgba(6,182,212,0.3)]'
                : 'text-slate-400 hover:text-cyan-400'
            }`}
          >
            <Zap size={12} />
            Connected ({stats.connected})
          </button>
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search company, URL, desc..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-900/80 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 font-mono transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* ── Partners Grid Showcase ─────────────────────────────────────── */}
      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-400 font-mono text-xs bg-slate-950/60 border border-white/10 rounded-2xl">
          <RefreshCw className="animate-spin text-amber-400" size={24} />
          <span>Synchronizing business partners ledger...</span>
        </div>
      ) : filteredPartners.length === 0 ? (
        <div className="py-16 text-center bg-slate-950/60 border border-dashed border-white/10 rounded-2xl p-8">
          <Handshake size={36} className="mx-auto text-slate-600 mb-3" />
          <h3 className="text-white font-mono font-bold text-sm">No Partners Found</h3>
          <p className="text-slate-400 text-xs font-mono mt-1 max-w-md mx-auto">
            {searchQuery
              ? `No partners match search '${searchQuery}'.`
              : tierFilter !== 'all'
              ? `No partners registered under the ${tierFilter} category yet.`
              : 'Add your first Official Business Partner or Connected Partner to display on the Kangavena homepage.'}
          </p>
          <button
            onClick={openAddModal}
            className="mt-4 px-4 py-2 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 font-mono font-bold text-xs hover:bg-amber-500/30 transition-all cursor-pointer"
          >
            + Add Partner Now
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredPartners.map((partner) => {
            const isOfficial = partner.tier === 'official';
            return (
              <div
                key={partner.id}
                className={`bg-slate-900/70 backdrop-blur-xl border rounded-2xl p-5 flex flex-col justify-between transition-all duration-300 group hover:scale-[1.01] relative overflow-hidden ${
                  isOfficial
                    ? 'border-amber-500/30 hover:border-amber-400/60 shadow-[0_0_25px_rgba(245,158,11,0.08)] hover:shadow-[0_0_35px_rgba(245,158,11,0.18)]'
                    : 'border-white/10 hover:border-cyan-500/40 hover:shadow-[0_0_30px_rgba(6,182,212,0.12)]'
                }`}
              >
                {/* Subtle Tier Background Ambient Glow */}
                <div
                  className={`absolute -top-12 -right-12 w-32 h-32 rounded-full blur-2xl pointer-events-none ${
                    isOfficial ? 'bg-amber-500/10' : 'bg-cyan-500/10'
                  }`}
                />

                {/* Top Bar: Logo & Tier Badges */}
                <div>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    {/* Logo Display */}
                    <div className="w-14 h-14 rounded-xl bg-slate-950 border border-white/10 p-2 flex items-center justify-center shrink-0 overflow-hidden shadow-inner group-hover:border-white/20 transition-colors">
                      {partner.logoUrl ? (
                        <img
                          src={partner.logoUrl}
                          alt={partner.name}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            (e.currentTarget as HTMLElement).style.display = 'none';
                            const fallback = e.currentTarget.parentElement?.querySelector('.fallback-icon');
                            if (fallback) (fallback as HTMLElement).style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className={`fallback-icon w-full h-full rounded-lg flex items-center justify-center font-mono font-black text-sm ${
                          partner.logoUrl ? 'hidden' : 'flex'
                        } ${
                          isOfficial
                            ? 'bg-amber-500/20 text-amber-300'
                            : 'bg-cyan-500/20 text-cyan-300'
                        }`}
                      >
                        {partner.name.slice(0, 2).toUpperCase()}
                      </div>
                    </div>

                    {/* Tier Badge & Order */}
                    <div className="flex flex-col items-end gap-1.5">
                      {isOfficial ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-black font-mono tracking-wider bg-gradient-to-r from-amber-500/20 to-amber-600/20 text-amber-300 border border-amber-500/40 shadow-[0_0_12px_rgba(245,158,11,0.2)]">
                          <Crown size={10} className="text-amber-400" />
                          OFFICIAL PARTNER
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-black font-mono tracking-wider bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                          <Zap size={10} className="text-cyan-400" />
                          CONNECTED PARTNER
                        </span>
                      )}

                      <span className="text-[9px] font-mono text-slate-400">
                        Sort Order: <strong className="text-white">#{partner.displayOrder}</strong>
                      </span>
                    </div>
                  </div>

                  {/* Company Name & Link */}
                  <h3 className="text-white font-black text-base tracking-tight font-sans flex items-center gap-1.5">
                    {partner.name}
                    {isOfficial && (
                      <ShieldCheck size={16} className="text-amber-400 shrink-0" title="Verified Kangavena Premium Partner" />
                    )}
                  </h3>

                  {partner.websiteUrl ? (
                    <a
                      href={partner.websiteUrl.startsWith('http') ? partner.websiteUrl : `https://${partner.websiteUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300 font-mono mt-1 transition-colors group/link break-all"
                    >
                      <Globe size={12} className="shrink-0 text-cyan-400" />
                      <span className="truncate max-w-[220px]">{partner.websiteUrl.replace(/^https?:\/\//, '')}</span>
                      <ExternalLink size={10} className="shrink-0 opacity-70 group-hover/link:translate-x-0.5 transition-transform" />
                    </a>
                  ) : (
                    <p className="text-xs text-slate-600 font-mono mt-1 italic">No website URL linked</p>
                  )}

                  {/* Description */}
                  <p className="text-slate-300 text-xs leading-relaxed mt-3 font-normal line-clamp-3">
                    {partner.description || 'No description provided.'}
                  </p>
                </div>

                {/* Bottom Action Controls */}
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-2">
                  {/* Status Toggle Button */}
                  <button
                    onClick={() => handleToggleActive(partner)}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                      partner.isActive
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30'
                        : 'bg-slate-800 text-slate-400 border border-slate-700 hover:text-white'
                    }`}
                    title="Click to toggle visibility on Kangavena Home"
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        partner.isActive ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'
                      }`}
                    />
                    <span>{partner.isActive ? 'LIVE ON HOME' : 'HIDDEN'}</span>
                  </button>

                  {/* Edit / Delete Buttons */}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => openEditModal(partner)}
                      className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all cursor-pointer"
                      title="Edit Partner"
                    >
                      <Edit2 size={13} />
                    </button>
                    <button
                      onClick={() => handleDelete(partner.id)}
                      disabled={deletingId === partner.id}
                      className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-400 hover:text-rose-300 transition-all cursor-pointer disabled:opacity-50"
                      title="Delete Partner"
                    >
                      {deletingId === partner.id ? (
                        <RefreshCw size={13} className="animate-spin" />
                      ) : (
                        <Trash2 size={13} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add / Edit Partner Modal ────────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto">
          <div className="bg-[#090d16] border border-slate-700 rounded-3xl p-6 sm:p-8 w-full max-w-xl shadow-[0_0_60px_rgba(0,0,0,0.8)] relative my-8 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between pb-4 mb-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400">
                  {editingPartner ? <Edit2 size={18} /> : <Handshake size={18} />}
                </div>
                <div>
                  <h3 className="text-base font-black text-white font-mono uppercase tracking-tight">
                    {editingPartner ? 'Edit Business Partner' : 'Register New Business Partner'}
                  </h3>
                  <p className="text-xs text-slate-400 font-mono">
                    Configure company identity, partner tier, and web presentation
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Partner Tier Selector */}
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-2">
                  Partner Classification / Tier *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Official Option */}
                  <label
                    className={`relative p-3.5 rounded-2xl border cursor-pointer transition-all flex flex-col gap-1.5 ${
                      formData.tier === 'official'
                        ? 'bg-amber-500/10 border-amber-500/60 shadow-[0_0_20px_rgba(245,158,11,0.2)]'
                        : 'bg-black/40 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <input
                      type="radio"
                      name="tier"
                      value="official"
                      checked={formData.tier === 'official'}
                      onChange={() => setFormData(prev => ({ ...prev, tier: 'official' }))}
                      className="sr-only"
                    />
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs font-black font-mono text-amber-300">
                        <Crown size={14} className="text-amber-400" /> OFFICIAL PARTNER
                      </span>
                      {formData.tier === 'official' && <Check size={14} className="text-amber-400 font-bold" />}
                    </div>
                    <p className="text-[11px] text-slate-400 leading-tight">
                      Kangavena Premium Business Partner. High-visibility gold glow and prime display.
                    </p>
                  </label>

                  {/* Connected Option */}
                  <label
                    className={`relative p-3.5 rounded-2xl border cursor-pointer transition-all flex flex-col gap-1.5 ${
                      formData.tier === 'connected'
                        ? 'bg-cyan-500/10 border-cyan-500/60 shadow-[0_0_20px_rgba(6,182,212,0.2)]'
                        : 'bg-black/40 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <input
                      type="radio"
                      name="tier"
                      value="connected"
                      checked={formData.tier === 'connected'}
                      onChange={() => setFormData(prev => ({ ...prev, tier: 'connected' }))}
                      className="sr-only"
                    />
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs font-black font-mono text-cyan-300">
                        <Zap size={14} className="text-cyan-400" /> CONNECTED PARTNER
                      </span>
                      {formData.tier === 'connected' && <Check size={14} className="text-cyan-400 font-bold" />}
                    </div>
                    <p className="text-[11px] text-slate-400 leading-tight">
                      Standard partner or ecosystem vendor integrated into the network.
                    </p>
                  </label>
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1.5">
                  Company Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Seylan Commercial Tech or Softlogic ERP"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/60 font-mono"
                />
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1.5">
                  Website URL
                </label>
                <div className="relative">
                  <Globe size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="url"
                    placeholder="https://company.com"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, websiteUrl: e.target.value }))}
                    className="w-full bg-slate-950 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/60 font-mono"
                  />
                </div>
              </div>

              {/* Logo Section: Upload Image or Direct URL */}
              <div className="space-y-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono">
                  Company Logo (Upload File or Enter URL)
                </label>

                <div className="flex flex-col sm:flex-row gap-3 items-start">
                  {/* File Upload Button */}
                  <div className="w-full sm:w-auto">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="partner-logo-file"
                    />
                    <label
                      htmlFor="partner-logo-file"
                      className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 bg-slate-900 hover:bg-slate-800 text-xs font-mono font-bold text-slate-200 hover:text-white cursor-pointer transition-all w-full sm:w-auto ${
                        uploadingLogo ? 'opacity-50 pointer-events-none' : ''
                      }`}
                    >
                      {uploadingLogo ? (
                        <RefreshCw size={14} className="animate-spin text-amber-400" />
                      ) : (
                        <Upload size={14} className="text-amber-400" />
                      )}
                      <span>{uploadingLogo ? 'Uploading...' : 'Upload Logo Image'}</span>
                    </label>
                  </div>

                  {/* Logo URL Input */}
                  <div className="flex-1 w-full">
                    <input
                      type="text"
                      placeholder="Or paste image URL (https://... or /uploads/...)"
                      value={formData.logoUrl}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, logoUrl: e.target.value }));
                        setLogoPreviewError(false);
                      }}
                      className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/60 font-mono"
                    />
                  </div>
                </div>

                {/* Logo Live Preview */}
                {formData.logoUrl && (
                  <div className="p-3 bg-black/50 border border-white/10 rounded-xl flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-slate-900 border border-white/10 p-1.5 flex items-center justify-center overflow-hidden shrink-0">
                      {!logoPreviewError ? (
                        <img
                          src={formData.logoUrl}
                          alt="Logo Preview"
                          className="w-full h-full object-contain"
                          onError={() => setLogoPreviewError(true)}
                        />
                      ) : (
                        <span className="text-[10px] text-rose-400 font-mono">Broken</span>
                      )}
                    </div>
                    <div className="text-xs font-mono">
                      <p className="text-slate-300 font-bold">Logo Preview</p>
                      <p className="text-[11px] text-slate-500 truncate max-w-[260px]">{formData.logoUrl}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1.5">
                  Company Bio / Description
                </label>
                <textarea
                  rows={3}
                  placeholder="Short overview of the partner's business operations and strategic partnership with Kangavena..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/60 font-sans leading-relaxed resize-none"
                />
              </div>

              {/* Display Order & Active Toggle */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1.5">
                    Display Priority Order
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={formData.displayOrder}
                    onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value, 10) || 0 }))}
                    className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white font-mono focus:outline-none focus:border-amber-500/60"
                  />
                  <span className="text-[10px] text-slate-500 font-mono mt-1 block">Lower numbers appear first (0, 1, 2...)</span>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest font-mono mb-1.5">
                    Visibility on Homepage
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                    className={`w-full py-2.5 px-4 rounded-xl border font-mono font-bold text-xs flex items-center justify-between transition-all cursor-pointer ${
                      formData.isActive
                        ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                        : 'bg-slate-900 border-white/10 text-slate-400'
                    }`}
                  >
                    <span>{formData.isActive ? 'Active (Visible on Home)' : 'Hidden (Draft Mode)'}</span>
                    <span
                      className={`w-3 h-3 rounded-full ${
                        formData.isActive ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]' : 'bg-slate-600'
                      }`}
                    />
                  </button>
                  <span className="text-[10px] text-slate-500 font-mono mt-1 block">Toggle to publish or hide instantly</span>
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-amber-500 via-emerald-600 to-teal-500 hover:from-amber-400 hover:to-teal-400 text-slate-950 font-black py-3 rounded-xl transition-all uppercase tracking-wider text-xs font-mono flex items-center justify-center gap-2 cursor-pointer shadow-[0_0_20px_rgba(245,158,11,0.3)] disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      <span>Saving Partner...</span>
                    </>
                  ) : (
                    <>
                      <Check size={15} />
                      <span>{editingPartner ? 'Update Partner' : 'Register Partner'}</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={saving}
                  className="px-6 py-3 border border-white/10 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition-all text-xs font-mono uppercase tracking-widest cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
